export const EMPLOYEE_STATUS_OPTIONS = ["Active", "On Hold", "Finished", "Rejected"];

const statusConfig = {
  active:    { label: "Active",   bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
  "on hold": { label: "On Hold",  bg: "#fffbeb", color: "#b45309", border: "#fde68a" },
  finished:  { label: "Finished", bg: "#f8fafc", color: "#475569", border: "#cbd5e1" },
  rejected:  { label: "Rejected", bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
};

function getStatusCfg(raw) {
  const key = String(raw || "active").trim().toLowerCase();
  return statusConfig[key] || { label: raw || "Unknown", bg: "#f8fafc", color: "#475569", border: "#cbd5e1" };
}

export const UI = {
  /* ── Shared page shell ── */
  page: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    padding: "24px 28px",
    boxSizing: "border-box",
    background: "#fafafa",
  },

  /* ── Shared controls ── */
  pageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
    flexWrap: "wrap",
    gap: 12,
  },
  pageTitle:    { fontSize: 22, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.3px" },
  pageSubtitle: { fontSize: 13, color: "#94a3b8", marginTop: 2, fontWeight: 400 },

  controlsBar: {
    display: "flex",
    gap: 10,
    marginBottom: 16,
    alignItems: "center",
    flexWrap: "wrap",
  },
  searchWrap:  { position: "relative", flex: "1 1 220px", minWidth: 180 },
  searchInput: {
    width: "100%",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    padding: "9px 36px 9px 14px",
    fontSize: 14,
    outline: "none",
    background: "#fff",
    color: "#0f172a",
    boxSizing: "border-box",
  },
  searchClear: {
    position: "absolute",
    right: 10,
    top: "50%",
    transform: "translateY(-50%)",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    color: "#94a3b8",
    fontSize: 16,
    lineHeight: 1,
    padding: "2px 4px",
    display: "flex",
    alignItems: "center",
  },
  select: {
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    padding: "9px 12px",
    fontSize: 13,
    outline: "none",
    background: "#fff",
    cursor: "pointer",
    color: "#374151",
    minWidth: 120,
  },
  iconBtn: {
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    padding: "9px 14px",
    cursor: "pointer",
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexShrink: 0,
  },

  /* ── Directory list ── */
  listCard: {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    overflow: "hidden",
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  listScroll: { overflow: "auto", flex: 1 },
  empRow: {
    display: "flex",
    alignItems: "center",
    padding: "14px 20px",
    cursor: "pointer",
    gap: 14,
  },
  avatarCircle: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    background: "#f1f5f9",
    border: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 600,
    color: "#475569",
    flexShrink: 0,
    letterSpacing: 0.5,
  },
  empInfo:  { flex: 1, minWidth: 0 },
  empName:  { fontSize: 14, fontWeight: 600, color: "#0f172a", lineHeight: 1.3 },
  empMeta:  { fontSize: 12, color: "#94a3b8", marginTop: 2, display: "flex", gap: 8, flexWrap: "wrap" },
  empRight: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 },
  roleChip: (role) => ({
    display: "inline-block",
    fontSize: 11,
    fontWeight: 500,
    color: role === "admin" ? "#7c3aed" : "#0369a1",
    background: role === "admin" ? "#f5f3ff" : "#f0f9ff",
    border: `1px solid ${role === "admin" ? "#ddd6fe" : "#bae6fd"}`,
    borderRadius: 6,
    padding: "2px 7px",
    textTransform: "capitalize",
  }),
  emptyState: {
    padding: "60px 20px",
    textAlign: "center",
    color: "#94a3b8",
    fontSize: 14,
  },

  /* ── Status badges ── */
  statusBadge: (raw) => {
    const cfg = getStatusCfg(raw);
    return {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      fontSize: 12,
      fontWeight: 500,
      color: cfg.color,
      background: cfg.bg,
      border: `1px solid ${cfg.border}`,
      borderRadius: 6,
      padding: "3px 8px",
      whiteSpace: "nowrap",
    };
  },
  statusDot: (raw) => {
    const cfg = getStatusCfg(raw);
    return { width: 6, height: 6, borderRadius: "50%", background: cfg.color, flexShrink: 0 };
  },

  /* ══════════════════════════════════
     DETAIL PAGE
  ══════════════════════════════════ */
  detailPage: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    background: "#fafafa",
  },
  detailTopBar: {
    padding: "14px 24px",
    display: "flex",
    alignItems: "center",
    gap: 14,
    borderBottom: "1px solid #e5e7eb",
    background: "#fff",
    flexShrink: 0,
    flexWrap: "wrap",
  },
  backBtn: {
    border: "1px solid #e2e8f0",
    background: "#fff",
    borderRadius: 9,
    padding: "8px 14px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
    color: "#374151",
    flexShrink: 0,
  },
  detailHeaderInfo: { flex: 1, minWidth: 160 },
  detailName:       { fontSize: 17, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.2px", margin: 0 },

  saveBtn: {
    border: "none",
    borderRadius: 9,
    padding: "9px 20px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
    background: "#0f172a",
    color: "#fff",
    flexShrink: 0,
  },
  saveBtnDisabled: { opacity: 0.5, cursor: "not-allowed" },

  detailScroll: {
    flex: 1,
    overflow: "auto",
    padding: "24px",
    boxSizing: "border-box",
  },
  /* Responsive two-column: wraps to single column on narrow viewports */
  detailColumns: {
    display: "flex",
    flexWrap: "wrap",
    gap: 18,
    maxWidth: 1040,
  },
  detailColumn: {
    flex: "1 1 360px",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 18,
  },

  /* ── Section cards ── */
  sectionCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    overflow: "hidden",
  },
  sectionHeader: { padding: "13px 20px 11px", borderBottom: "1px solid #f1f5f9" },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.7px",
  },
  sectionBody: { padding: "2px 0" },

  /* ── Field rows ── */
  fieldRow: {
    display: "flex",
    alignItems: "center",
    padding: "11px 20px",
    gap: 16,
    minWidth: 0,
  },
  fieldLabel: {
    fontSize: 13,
    color: "#6b7280",
    fontWeight: 400,
    width: 140,
    flexShrink: 0,
  },
  /* Standard text input — full width inside the flex cell */
  fieldInput: {
    flex: 1,
    minWidth: 0,
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: "7px 11px",
    fontSize: 14,
    color: "#0f172a",
    outline: "none",
    background: "#fff",
    fontWeight: 400,
    boxSizing: "border-box",
    width: "100%",
  },
  /* Date input — slightly narrower but still proportional */
  fieldInputDate: {
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: "7px 11px",
    fontSize: 14,
    color: "#0f172a",
    outline: "none",
    background: "#fff",
    fontWeight: 400,
    boxSizing: "border-box",
    width: "100%",
    maxWidth: 200,
  },
  fieldSelect: {
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: "7px 11px",
    fontSize: 14,
    outline: "none",
    background: "#fff",
    cursor: "pointer",
    color: "#0f172a",
    fontWeight: 400,
  },
  /* Currency rate inputs */
  rateWrap: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flex: 1,
    minWidth: 0,
  },
  rateCurrency: { fontSize: 13, color: "#6b7280", fontWeight: 500, flexShrink: 0 },
  rateInput: {
    flex: 1,
    minWidth: 0,
    maxWidth: 120,
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: "7px 11px",
    fontSize: 14,
    color: "#0f172a",
    outline: "none",
    background: "#fff",
    fontWeight: 400,
    boxSizing: "border-box",
  },
  rateUnit: { fontSize: 13, color: "#94a3b8", fontWeight: 400, flexShrink: 0 },

  /* Notes */
  notesCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    overflow: "hidden",
  },
  notesTextarea: {
    width: "100%",
    border: "none",
    outline: "none",
    resize: "none",
    background: "transparent",
    fontSize: 14,
    color: "#0f172a",
    lineHeight: 1.6,
    padding: "14px 20px",
    boxSizing: "border-box",
    minHeight: 140,
    fontFamily: "inherit",
  },

  /* ══════════════════════════════════
     PAY RATE PAGE
  ══════════════════════════════════ */
  prPage: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    padding: "24px 28px",
    boxSizing: "border-box",
    background: "#fafafa",
  },
  prListCard: {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    overflow: "hidden",
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  prListScroll: { overflow: "auto", flex: 1 },
  prRow: {
    display: "flex",
    alignItems: "center",
    padding: "16px 20px",
    gap: 16,
    flexWrap: "wrap",
  },
  prEmployeeCell: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flex: "1 1 200px",
    minWidth: 0,
  },
  prRatesCell: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    flex: "0 0 auto",
  },
  prRateGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  prRateLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  prRateInputWrap: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    overflow: "hidden",
    background: "#fff",
  },
  prRatePrefix: {
    padding: "7px 8px 7px 10px",
    fontSize: 13,
    color: "#6b7280",
    fontWeight: 500,
    background: "#f8fafc",
    borderRight: "1px solid #e5e7eb",
    flexShrink: 0,
  },
  prRateInputEl: {
    border: "none",
    outline: "none",
    padding: "7px 8px",
    fontSize: 14,
    color: "#0f172a",
    width: 80,
    background: "#fff",
    fontFamily: "inherit",
  },
  prRateSuffix: {
    padding: "7px 10px 7px 4px",
    fontSize: 12,
    color: "#94a3b8",
    background: "#fff",
    flexShrink: 0,
  },
  prSaveBtn: {
    border: "none",
    borderRadius: 8,
    padding: "8px 16px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
    background: "#0f172a",
    color: "#fff",
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  prSaveBtnDisabled: {
    background: "#e2e8f0",
    color: "#94a3b8",
    cursor: "not-allowed",
  },
};
